import { MIN_ADULTS } from '../constants/tripSearchParty';
import { EMPTY_TRIP_SEARCH, TripSearch, isDefaultParty } from '../contexts/TripSearchContext';
import { parseIsoDateLocal } from './formatters';

export type SearchTab = 'discover' | 'stays' | 'services';

export interface TripSearchSummary {
  /** One-line bar text; empty when nothing is set so the bar shows its prompt. */
  barLine: string;
  whereValue: string;
  whenValue: string;
  whoValue: string;
}

export const SEARCH_RESULTS_PATH = '/search';
/** Results URL key that narrows /search to stays only; any other value means combined results. */
export const RESULTS_KIND_PARAM = 'kind';
export const STAYS_ONLY_KIND = 'stays';
export const STAYS_BROWSE_PATH = '/accommodations';
export const SERVICES_PATH = '/services';

const WHEN_PROMPT = 'Add dates';
const SUMMARY_SEPARATOR = ' · ';

/** The city part of a destination such as "Aspen, Colorado". */
export function cityTokenOf(destination: string): string {
  return destination.split(',')[0].trim();
}

/** Where the sheet's Search button lands, with the picked values as query params. */
export function buildSearchResultsPath(
  tab: SearchTab,
  trip: TripSearch,
  typedText: string,
): string {
  const params = new URLSearchParams();
  const trimmedText = typedText.trim();
  if (trip.destination) params.set('destination', trip.destination);
  else if (trimmedText) params.set('search', trimmedText);
  if (trip.checkin) params.set('checkin', trip.checkin);
  if (trip.checkout) params.set('checkout', trip.checkout);
  if (!isDefaultParty(trip)) {
    params.set('guests', String(trip.guests));
    params.set('adults', String(trip.adults));
    if (trip.children > 0) params.set('children', String(trip.children));
  }
  const path = landingPathFor(tab, hasStayCriteria(trip, trimmedText));
  if (tab === 'stays' && path === SEARCH_RESULTS_PATH)
    params.set(RESULTS_KIND_PARAM, STAYS_ONLY_KIND);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** The one rule for a stays search worth listing: a place, typed text or a non-default party. */
function hasStayCriteria(trip: TripSearch, typedText: string): boolean {
  return Boolean(trip.destination || typedText || !isDefaultParty(trip));
}

/** Stays and Discover list matches on /search once a place, text or party is set; Services keeps its page. */
function landingPathFor(tab: SearchTab, hasCriteria: boolean): string {
  if (tab === 'services') return SERVICES_PATH;
  return hasCriteria ? SEARCH_RESULTS_PATH : STAYS_BROWSE_PATH;
}

function formatShortDate(iso: string): string {
  const date = parseIsoDateLocal(iso);
  if (!date) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDates(trip: TripSearch): string {
  if (trip.checkin && trip.checkout) {
    return `${formatShortDate(trip.checkin)} – ${formatShortDate(trip.checkout)}`;
  }
  if (trip.checkin) return formatShortDate(trip.checkin);
  return '';
}

/** The one party wording: "1 guest" or "N guests". */
export function formatPartyCount(guests: number): string {
  return guests === 1 ? '1 guest' : `${guests} guests`;
}

/** A results heading names what was searched: a place, else the text, else the party. */
export function searchResultsHeading(params: URLSearchParams): string {
  const destination = params.get('destination');
  if (destination) return `In ${cityTokenOf(destination)}`;
  const search = params.get('search');
  if (search) return `Results for "${search}"`;
  return `Results for ${formatPartyCount(tripSearchFromParams(params).guests)}`;
}

/** How each tab names its first search field: a place, a service, or either. */
export interface WhereFieldCopy {
  /** The field's name: visible on the phone card, screen-reader only on the desktop bar. */
  label: string;
  /** The question heading the phone card while it is open. */
  question: string;
  /** The value shown while nothing is picked or typed. */
  prompt: string;
  /** The text input's placeholder and accessible name. */
  placeholder: string;
  /** The heading over the listings that match the typed text. */
  matchesHeading: string;
  /** The heading over example listings shown before anything is typed, on tabs that offer them. */
  suggestedListingsHeading?: string;
}

export const WHERE_FIELD_COPY: Record<SearchTab, WhereFieldCopy> = {
  discover: {
    label: 'Where or what',
    question: 'Where, or what?',
    prompt: 'Places or services',
    placeholder: 'Search places, stays or services',
    matchesHeading: 'Stays & services',
  },
  stays: {
    label: 'Where',
    question: 'Where to?',
    prompt: 'Anywhere',
    placeholder: 'Search destinations or stays',
    matchesHeading: 'Stays & services',
  },
  services: {
    label: 'What',
    question: 'What do you need?',
    prompt: 'Any service',
    placeholder: 'Search services or places',
    matchesHeading: 'Services',
    suggestedListingsHeading: 'Suggested services',
  },
};

/** The bar's one-line summary and the three collapsed-card values. */
export function summariseTripSearch(
  trip: TripSearch,
  typedText: string,
  tab: SearchTab,
): TripSearchSummary {
  const where = trip.destination || typedText.trim();
  const dates = formatDates(trip);
  const party = formatPartyCount(trip.guests);
  const barParty = isDefaultParty(trip) ? '' : party;
  const barLine = [cityTokenOf(where), dates, barParty].filter(Boolean).join(SUMMARY_SEPARATOR);
  return {
    barLine,
    whereValue: where || WHERE_FIELD_COPY[tab].prompt,
    whenValue: dates || WHEN_PROMPT,
    whoValue: party,
  };
}

const ASPEN_SNOWMASS_SERVICE_LOCATIONS = ['Aspen', 'Snowmass'];

/**
 * The service locations a destination city token should match. Aspen and
 * Snowmass share one service area, so either town matches both; any other
 * city matches itself.
 */
export function serviceLocationsForCity(cityToken: string): string[] {
  const trimmedCity = cityToken.trim();
  const isAspenOrSnowmass = ASPEN_SNOWMASS_SERVICE_LOCATIONS.some(
    (town) => town.toLowerCase() === trimmedCity.toLowerCase(),
  );
  return isAspenOrSnowmass ? [...ASPEN_SNOWMASS_SERVICE_LOCATIONS] : [trimmedCity];
}

/** Params for `getAccommodations` that a stays results URL stands for. */
export interface StaySearchParams {
  location?: string;
  search?: string;
  min_guests?: number;
  sort_by: 'rating';
  order: 'asc' | 'desc';
}

function countParam(params: URLSearchParams, name: string, minimum: number): number {
  const parsed = Number.parseInt(params.get(name) ?? '', 10);
  return Number.isNaN(parsed) ? minimum : Math.max(minimum, parsed);
}

export function tripSearchFromParams(params: URLSearchParams): TripSearch {
  const adults = countParam(params, 'adults', MIN_ADULTS);
  const children = countParam(params, 'children', 0);
  return {
    destination: params.get('destination') ?? EMPTY_TRIP_SEARCH.destination,
    checkin: params.get('checkin'),
    checkout: params.get('checkout'),
    guests: adults + children,
    adults,
    children,
  };
}

/** Whether a results URL names a place, typed text or a non-default party. */
export function hasStaySearchCriteria(params: URLSearchParams): boolean {
  return hasStayCriteria(tripSearchFromParams(params), params.get('search') ?? '');
}

export function staySearchParamsFrom(params: URLSearchParams): StaySearchParams {
  const trip = tripSearchFromParams(params);
  const search = params.get('search');
  return {
    ...(trip.destination ? { location: cityTokenOf(trip.destination) } : {}),
    ...(search ? { search } : {}),
    ...(isDefaultParty(trip) ? {} : { min_guests: trip.guests }),
    sort_by: 'rating',
    order: 'desc',
  };
}

export function isStaysOnlySearch(params: URLSearchParams): boolean {
  return params.get(RESULTS_KIND_PARAM) === STAYS_ONLY_KIND;
}

/** Params for `searchServices` that a combined results URL stands for. */
export interface ServiceSearchParams {
  location?: string[];
  search?: string;
}

/** Services match on place and text only; party, dates and sort do not apply. */
export function serviceSearchParamsFrom(params: URLSearchParams): ServiceSearchParams {
  const destination = params.get('destination');
  const search = params.get('search');
  return {
    ...(destination ? { location: serviceLocationsForCity(cityTokenOf(destination)) } : {}),
    ...(search ? { search } : {}),
  };
}

/** Whether a results URL names a place or text a service search can match. */
export function hasServiceSearchCriteria(params: URLSearchParams): boolean {
  return Boolean(params.get('destination') || params.get('search'));
}

/** The same search, listed on the stays-only results page. */
export function staysSeeAllPath(params: URLSearchParams): string {
  const staysParams = new URLSearchParams(params);
  staysParams.delete(RESULTS_KIND_PARAM);
  staysParams.set(RESULTS_KIND_PARAM, STAYS_ONLY_KIND);
  return `${SEARCH_RESULTS_PATH}?${staysParams.toString()}`;
}

/** The Services page, carrying only the place and text a service search uses. */
export function servicesSeeAllPath(params: URLSearchParams): string {
  const servicesParams = new URLSearchParams();
  const search = params.get('search');
  const destination = params.get('destination');
  if (search) servicesParams.set('search', search);
  if (destination) servicesParams.set('destination', destination);
  const query = servicesParams.toString();
  return query ? `${SERVICES_PATH}?${query}` : SERVICES_PATH;
}
